import React, { useRef, useState } from 'react';
import styles from './styles.module.css';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import ZoomableImage from './ZoomableImage';
import { useAuthFileUrl } from '../../../../components/patient-attachments/AuthFile';
import { formatPhotoDate } from '../../modules/evolutionPhotos';

/**
 * Comparação por controle deslizante: a foto "depois" por cima da "antes",
 * recortada na posição do divisor. O divisor arrasta pelo botão redondo e
 * também pelo controle abaixo (alternativa por teclado e no celular); arrastar
 * fora do divisor move as duas fotos juntas (mesmo zoom do lado a lado).
 */
const EvolutionCompareSlider = ({ before, after, transform, onTransform }) => {
  const [position, setPosition] = useState(50);
  const stageRef = useRef(null);
  const afterFile = useAuthFileUrl(after ? after.id : null, 'full', Boolean(after));

  function positionFromEvent(event) {
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    const next = ((event.clientX - rect.left) / rect.width) * 100;
    setPosition(Math.min(100, Math.max(0, next)));
  }

  function handleDividerDown(event) {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    positionFromEvent(event);
  }

  function handleDividerMove(event) {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    positionFromEvent(event);
  }

  function handleDividerUp(event) {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  const { scale, x, y } = transform;

  return (
    <div className={styles.sliderWrap} ref={stageRef}>
      <ZoomableImage attachment={before} transform={transform} onTransform={onTransform}>
        <div className={styles.sliderLayer} style={{ clipPath: `inset(0 0 0 ${position}%)` }}>
          {afterFile.url && (
            <img
              className={styles.stageImage}
              src={afterFile.url}
              alt={after.title || 'Depois'}
              style={{ transform: `translate(${x}px, ${y}px) scale(${scale})` }}
              draggable={false}
            />
          )}
        </div>

        <span className={styles.sliderTag} style={{ left: 8 }}>{`Antes · ${formatPhotoDate(before)}`}</span>
        <span className={styles.sliderTag} style={{ right: 8 }}>{`Depois · ${formatPhotoDate(after)}`}</span>

        <div
          className={styles.sliderDivider}
          style={{ left: `${position}%` }}
          onPointerDown={handleDividerDown}
          onPointerMove={handleDividerMove}
          onPointerUp={handleDividerUp}
          onPointerCancel={handleDividerUp}
        >
          <span className={styles.sliderKnob}>
            <UnfoldMoreIcon fontSize="small" sx={{ transform: 'rotate(90deg)' }} />
          </span>
        </div>
      </ZoomableImage>

      <input
        type="range"
        min={0}
        max={100}
        value={Math.round(position)}
        className={styles.sliderRange}
        aria-label="Posição do divisor entre antes e depois"
        onChange={(event) => setPosition(Number(event.target.value))}
      />
    </div>
  );
};

export default EvolutionCompareSlider;
